import { DEFAULT_TENANT } from './../../../config';
import { AuthService } from '../auth/auth.service';
import { Router, NavigationExtras } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { environment } from '../../../environments/environment';
import { StorageService } from '../storage/storage.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';

@Injectable()
export class ConnectService {
  public localRoot = 'assets/json/';
  public headers = new HttpHeaders();
  public _apiRoot = '';
  public _apiBase = 'participant';
  DEFAULT_TENANT = DEFAULT_TENANT;

  constructor(
    public http: HttpClient,
    public router: Router,
    public authSvc: AuthService,
    private connectivityService: ConnectivityService,
    public storageSvc: StorageService
  ) {
    this.getURL();
  }

  public async setWeGuideApi() {
    this._apiRoot = `https://public.${environment.API_HOST}/api/` + this._apiBase;
  }

  public async setOrganisationApi() {
    this._apiRoot = `https://${this.authSvc.organization}.${environment.API_HOST}/api/` + this._apiBase;
  }

  public async setSaveURL(isParticipant: boolean) {
    this._apiBase = isParticipant ? 'participant' : 'v1';
    await this.storageSvc.set('url', this._apiBase);
  }

  public getToken(email: string, password: string) {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post(`${this._apiRoot}login`, `email=${email}password=${password}`, {
      headers,
    });
  }

  public setAuthenticatedHeaderAndUrl() {
    let token = this.authSvc.token;
    this.authSvc.readToken().then(storageToken => (token = storageToken));
    this.headers = this.headers.set('Content-Type', 'application/json');
    this.headers = this.headers.set('Accept', 'application/json');
    this.headers = this.headers.set('Authorization', `Bearer ${token}`);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    !this.authSvc.organization ? this.setWeGuideApi() : this.setOrganisationApi();
  }

  public getURL() {
    this.storageSvc.get('url').then(url => {
      if (url) {
        this._apiBase = url;
      }
    });
  }

  public list(object: string) {
    this.setAuthenticatedHeaderAndUrl();
    return this.http.get(this._apiRoot + object, { headers: this.headers });
  }

  public localList(object: string) {
    return this.http.get(this.localRoot + object);
  }

  public get(object: string, id: string) {
    this.setAuthenticatedHeaderAndUrl();
    return this.http.get(`${this._apiRoot}${object}/${id}`, { headers: this.headers });
  }

  public getCustom(object: string) {
    this.setAuthenticatedHeaderAndUrl();
    return this.http.get(this._apiRoot + object, {
      headers: this.headers,
    });
  }

  public create(object: string, parameters: object) {
    this.setAuthenticatedHeaderAndUrl();
    const data: string = JSON.stringify(parameters);
    return this.http.post(this._apiRoot + object, data, { headers: this.headers });
  }

  public update(object: string, id: number, parameters: object) {
    this.setAuthenticatedHeaderAndUrl();
    const data: string = JSON.stringify(parameters);
    if (id !== null) {
      return this.http.put(`${this._apiRoot}${object}/${id}`, data, { headers: this.headers });
    }

    return this.http.put(`${this._apiRoot}${object}`, data, { headers: this.headers });
  }

  public delete(object: string, id: string) {
    this.setAuthenticatedHeaderAndUrl();
    return this.http.delete(`${this._apiRoot}${object}/${id}`, { headers: this.headers });
  }

  public deleteCustom(object: string, body: object) {
    this.setAuthenticatedHeaderAndUrl();
    return this.http.request('DELETE', `${this._apiRoot}${object}`, { body, headers: this.headers });
  }

  handleError(error) {
    if (!this.connectivityService.isOnline()) {
      return this.router.navigateByUrl('error/503');
    }

    if (error.status === 401) {
      if (this.authSvc.token) {
        this.authSvc.removeCredentials();
        return this.router.navigateByUrl('error/401');
      }
      return;
    }

    if (error.status === 422) {
      const navigationExtras: NavigationExtras = {
        queryParams: {
          isVerification: 'true',
        },
      };
      return this.router.navigate(['error/' + error.status], navigationExtras);
    }

    if (error.status) {
      return this.router.navigateByUrl('error/' + error.status);
    }

    return this.router.navigateByUrl('error');
  }

  get isParticipant(): boolean {
    return this._apiBase === 'participant';
  }

  get apiRoot(): string {
    return this._apiRoot;
  }

  async refreshSaveURL(tenant?: string) {
    const organization = (await this.authSvc.getAuthDetails())?.organization;
    if (!tenant && !this.DEFAULT_TENANT && !organization) return;
    try {
      const response = await this.http
        .get(`https://public.${environment.API_HOST}/api/v1/accounts/complex_program_feature`, {
          headers: this.headers,
          params: {
            tenant: tenant || organization || this.DEFAULT_TENANT,
          },
        })
        .toPromise();
      if (typeof response === 'boolean') {
        this.setSaveURL(response === true);
      }
    } catch (error) {}
  }
}
